/**
 * COPYRIGHT & DMCA POLICY PAGE
 * 
 * Policy covering intellectual property, user content rights,
 * and DMCA takedown procedures
 * 
 * Special emphasis on respecting swatchers' photo rights
 * Should be reviewed by legal counsel before launch
 */

import { Copyright } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/common';

export default function CopyrightPolicyPage() {
  const lastUpdated = 'January 2025';

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <PageHeader
          icon={Copyright}
          title="Copyright & DMCA Policy"
          subtitle={`Last updated: ${lastUpdated}`}
          className="mb-6"
        />

        {/* Content */}
        <div className="glass-card p-8 space-y-8 font-body">
          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Our Commitment to Creators</h2>
            <p className="text-glass-secondary leading-relaxed">
              Polish ALL the Things deeply respects the nail art community and the creative work that 
              goes into it. We recognize that swatch photos, nail art images, and other visual content 
              represent significant time, skill, and artistic effort. This policy outlines how we 
              protect creators' rights and handle copyright concerns.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Your Content, Your Rights</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              When you upload photos or other content to Polish ALL the Things:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li><strong>You retain full ownership</strong> of your original content</li>
              <li><strong>You keep all copyrights</strong> to photos you've taken</li>
              <li>You grant us a limited license to display your content within the Service (see below)</li>
              <li>You can delete your content at any time</li>
              <li>If you delete your account, your content is removed from our servers</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">License to Display Your Content</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              By uploading content, you grant Polish ALL the Things a limited license to:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li>Display your content within the app according to your privacy settings</li>
              <li>Create thumbnails and optimized versions for performance</li>
              <li>Show your public content on your profile page if you've enabled sharing</li>
              <li>Include your content in search results within the platform</li>
            </ul>
            <p className="text-glass-secondary leading-relaxed mt-3">
              This license is <strong>non-exclusive</strong> (you can share your content anywhere else),
              <strong> revocable</strong> (ends when you delete the content or your account), and
              <strong> limited to operating the Service</strong> (we won't use your photos for
              advertising or sell them to third parties).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Respecting Swatchers' Rights</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              We understand the nail polish community has strong norms around crediting swatchers. 
              On Polish ALL the Things:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li><strong>Only upload photos you have the right to use</strong> — your own photos, 
                  or photos you have explicit permission to use</li>
              <li><strong>Brand promotional images</strong> are generally acceptable for cataloging 
                  purposes, but credit the source when possible</li>
              <li><strong>Other swatchers' photos</strong> should not be uploaded without their permission</li>
              <li>When in doubt, take your own swatch photos or ask permission</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">What's Not Allowed</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              Users may not upload content that:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li>Infringes on others' copyrights, trademarks, or other intellectual property rights</li>
              <li>Uses others' swatch photos without permission</li>
              <li>Removes or alters watermarks or attribution from images</li>
              <li>Falsely claims ownership of content created by others</li>
              <li>Violates any applicable laws regarding intellectual property</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">DMCA Takedown Procedure</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              If you believe your copyrighted work has been used on Polish ALL the Things without 
              authorization, you may submit a DMCA takedown notice. Your notice must include:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li>Your physical or electronic signature</li>
              <li>Identification of the copyrighted work you claim has been infringed</li>
              <li>Identification of the material you claim is infringing, with enough detail for us to locate it</li>
              <li>Your contact information (address, phone number, email)</li>
              <li>A statement that you have a good faith belief that the use is not authorized by the copyright owner</li>
              <li>A statement, under penalty of perjury, that the information in your notice is accurate and that you are the copyright owner or authorized to act on their behalf</li>
            </ul>
            <p className="text-glass-secondary leading-relaxed mt-3">
              Send DMCA notices to:{' '}
              <a href="mailto:hello@polishallthethings.com?subject=DMCA%20Takedown%20Notice" className="text-glass hover:underline">
                hello@polishallthethings.com
              </a>
              {' '}with the subject line "DMCA Takedown Notice"
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Counter-Notification</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              If you believe your content was removed by mistake or misidentification, you may submit 
              a counter-notification including:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li>Your physical or electronic signature</li>
              <li>Identification of the material that was removed and its previous location</li>
              <li>A statement under penalty of perjury that you have a good faith belief the material was removed by mistake</li>
              <li>Your name, address, and phone number</li>
              <li>A statement that you consent to the jurisdiction of federal court in your district</li>
              <li>A statement that you will accept service of process from the person who filed the original complaint</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Repeat Infringer Policy</h2>
            <p className="text-glass-secondary leading-relaxed">
              We take copyright infringement seriously. Users who repeatedly infringe on others' 
              copyrights will have their accounts terminated. We track DMCA complaints and reserve 
              the right to terminate accounts with multiple valid complaints, even if 
              counter-notifications are not filed.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Brand & Trademark Content</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              Polish ALL the Things is an independent collection management tool and is not affiliated 
              with any nail polish brands. Regarding brand content:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li>Brand names and logos are trademarks of their respective owners</li>
              <li>Brand names are used for identification and cataloging purposes only</li>
              <li>Official brand images should be attributed to the brand when possible</li>
              <li>Brands may request removal of their content by contacting us</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Questions?</h2>
            <p className="text-glass-secondary leading-relaxed">
              If you have questions about copyright, content ownership, or need to report an issue, 
              please contact us at{' '}
              <a href="mailto:hello@polishallthethings.com" className="text-glass hover:underline">
                hello@polishallthethings.com
              </a>
            </p>
          </section>

          {/* Related Links */}
          <section className="pt-4 border-t border-glass-border">
            <p className="text-glass-muted text-sm">
              Related policies:{' '}
              <Link to="/terms" className="text-glass hover:underline">Terms of Service</Link>
              {' · '}
              <Link to="/privacy" className="text-glass hover:underline">Privacy Policy</Link>
              {' · '}
              <Link to="/refund-policy" className="text-glass hover:underline">Refund Policy</Link>
            </p>
          </section>
        </div>

        {/* Back to top */}
        <div className="text-center mt-8">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="text-on-bg font-body"
          >
            ↑ Back to top
          </button>
        </div>
      </div>
    </div>
  );
}
